import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from './app';
import ErrorMessage from '@/models/Shared/ErrorMessage';
import Errors from '@/models/Shared/Errors';
import AnonJobWorkerInterviewDto from '@/models/AnonJobWorkers/AnonJobWorkerInterviewDto';
import AnonJobWorkerInterviewContactConfirmationReply from '@/models/AnonJobWorkers/AnonJobWorkerInterviewContactConfirmationReply';
import AnonJobWorkerOfferDto from '@/models/AnonJobWorkers/AnonJobWorkerOfferDto';
import AnonJobWorkerOfferContactConfirmationReply from '@/models/AnonJobWorkers/AnonJobWorkerOfferContactConfirmationReply';

export interface IContactAnonState {
  isJobInterviewConfirmationSubmitted: boolean;
  isJobOfferConfirmationSubmitted: boolean;
  interviewConfirmation: AnonJobWorkerInterviewDto;
  offerConfirmation: AnonJobWorkerOfferDto;
  isError: boolean;
  errorMessage: string;
  loading: boolean; 
  loadingDialog: boolean;
}

@Module({ dynamic: true, store, name: 'ContactAnon', namespaced: true })
class ContactAnon extends VuexModule implements IContactAnonState {
  public isJobInterviewConfirmationSubmitted = false;
  public isJobOfferConfirmationSubmitted = false;
  public interviewConfirmation: AnonJobWorkerInterviewDto = new AnonJobWorkerInterviewDto
  public offerConfirmation: AnonJobWorkerOfferDto = new AnonJobWorkerOfferDto
  public isError = false; 
  public errorMessage = '';
  public loading = true;
  public loadingDialog = false;

  @Action
  async optOutOfMarketing(urlPath: string) {
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/anon-contact/marketing-opt-out/${urlPath}`)
    .then(response => {
      this.setLoading(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  // Opt-out for an opportunity contact (snapshot prospect). Same anon flow as
  // optOutOfMarketing but hits the opportunity endpoint.
  @Action
  async optOutOfMarketingOpportunity(urlPath: string) {
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/anon-opportunity/marketing-opt-out/${urlPath}`)
    .then(response => {
      this.setLoading(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async getInterviewConfirmation(jobWorkerInterviewId: string) {
    this.setLoading(true);
    
    await ajax.get(`/api/v1/anon-contact/job-interviews/${jobWorkerInterviewId}`)
    .then(response => {
      this.context.commit("setInterviewConfirmation", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoading(false);
    });
  }


  @Action 
  async interviewContactConfirmation(request: AnonJobWorkerInterviewContactConfirmationReply) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/anon-contact/job-interviews/${ContactAnonModule.interviewConfirmation.jobWorkerInterviewId}`, request)
    .then(response => {
      this.context.commit("setSubmittedInterviewConfirmation");
      this.setLoadingDialog(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async getOfferConfirmation(jobWorkerOfferId: string) {
    this.setLoading(true);
    
    await ajax.get(`/api/v1/anon-contact/job-offers/${jobWorkerOfferId}`)
    .then(response => {
      this.context.commit("setOfferConfirmation", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoading(false);
    });
  }


  @Action 
  async offerContactConfirmation(request: AnonJobWorkerOfferContactConfirmationReply) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/anon-contact/job-offers/${ContactAnonModule.offerConfirmation.jobWorkerOfferId}`, request)
    .then(response => {
      this.context.commit("setSubmittedOfferConfirmation");
      this.setLoadingDialog(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async handleApiError(error: any) {

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx. response.Data, response.status and response.headers available
      //For anonymous users we keep it really simple...
      if (error.response.data.code == Errors.Forbidden) {
        this.context.commit("setErrorMessage", new ErrorMessage("Sorry, this link has expired.", error.response.data.code, true));
      } else {
        this.context.commit("setErrorMessage", new ErrorMessage("Sorry, an unexpected error occured.", error.response.data.code, false));
      }
    }    
    this.context.commit("setErrorMessage", new ErrorMessage("Sorry, an unexpected error occured.", "", false));
  }

  @Action
  cleanState() {
      this.context.commit("resetState");
  }

  @Mutation
  resetState() {
    this.loading = true;
    this.loadingDialog = false;
    this.interviewConfirmation = new AnonJobWorkerInterviewDto();
    this.offerConfirmation = new AnonJobWorkerOfferDto();
    this.isJobInterviewConfirmationSubmitted = false;
    this.isJobOfferConfirmationSubmitted = false;
    this.isError = false;
    this.errorMessage = '';
  }

  @Mutation
  private async setErrorMessage(error: ErrorMessage) {
      this.isError = true;
      this.errorMessage = error.message;
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Mutation
  private async setLoadingDialog(isLoading: boolean) {
      this.loadingDialog = isLoading;
  }

  @Mutation
  private async setSubmittedInterviewConfirmation() {
      this.isError = false;
      this.errorMessage = '';
      this.isJobInterviewConfirmationSubmitted = true;
  }

  @Mutation
  private async setInterviewConfirmation(interviewConfirmation: AnonJobWorkerInterviewDto) {
      this.interviewConfirmation = interviewConfirmation;
      this.isError = false;
      this.errorMessage = '';
  }

  @Mutation
  private async setSubmittedOfferConfirmation() {
      this.isError = false;
      this.errorMessage = '';
      this.isJobOfferConfirmationSubmitted = true;
  }

  @Mutation
  private async setOfferConfirmation(offerConfirmation: AnonJobWorkerOfferDto) {
      this.offerConfirmation = offerConfirmation;
      this.isError = false;
      this.errorMessage = '';
  }
}

export const ContactAnonModule = getModule(ContactAnon)

